import PageHeader from "components/common/PageHeader";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { WashHistoryStatusBadge } from "components/common/StatusBadge";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect } from "react";
import { Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { formatUTCDate } from "utils/utils";
import InfoItem from "components/common/detailsPage/InfoItem";
import { getWashHistoryDetails } from "store/slices/washHistorySlice";
import { PLAN_OPTIONS } from "utils/constants";

const WashHistoryDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");

  const { canAction } = usePermission();
  const { canView } = canAction("wash_history");

  const { loading, itemDetails } = useSelector((state) => state.WashHistory);

  const {
    userId,
    userVehicleId,
    licensePlateNumber,
    branchId,
    planId,
    status,
    startedAt,
    completedAt,
    platform,
    createdAt,
    userMembershipId,
  } = itemDetails || {};

  const { makeId, modelId, colorId } = userVehicleId || {};

  useEffect(() => {
    if (id && canView) {
      dispatch(getWashHistoryDetails(id));
    }
  }, [id, canView]);

  const formatVehicleName = ({ make, model, color, language = "en" }) =>
    [make, model, color]
      .map((item) => item?.name?.[language])
      .filter(Boolean)
      .join(" ");

  const infoFields = [
    {
      label: t("Customer Name"),
      value: userId?.name || "-",
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Customer Phone Number"),
      value: userId?.phone || "-",
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Vehicle Name"),
      value: formatVehicleName({
        make: makeId,
        model: modelId,
        color: colorId,
        language: language,
      }),
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Status"),
      render: <WashHistoryStatusBadge status={status} t={t} />,
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    { isSpacer: true },
    {
      label: t("Number Plate"),
      value: licensePlateNumber,
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Wash Type"),
      value: PLAN_OPTIONS?.find(
        (option) => option?.value === userMembershipId?.planType,
      )?.name,
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Membership"),
      value: planId?.title?.en,
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Branch Name"),
      value: branchId?.name?.en || "-",
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    { isSpacer: true },
    {
      label: t("Platform"),
      value: platform,
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Wash Started Date"),
      value: formatUTCDate(startedAt),
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Wash Completed Date"),
      value: formatUTCDate(completedAt),
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
    {
      label: t("Created Date"),
      value: formatUTCDate(createdAt),
      col: { lg: 6, xs: 6 },
      className: "info_item_col",
    },
  ];
  const shimmerLayout = [
    {
      col: 12,
      blocks: [{ rows: 3, cols: 4 }],
    },
  ];

  if (!canView) return null;

  if (loading) {
    return (
      <div className="page-content">
        <PageHeader
          title={t("Wash History Details")}
          isBackBtn
          className="details_page_title"
        />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page customer_details">
      <PageHeader
        title={t("Wash History Details")}
        isBackBtn
        className="details_page_title"
      />

      <div className="common_card">
        <Row className="details_page_item_row">
          {infoFields.map((item, index) => {
            if (item.isSpacer) {
              return <div key={index} className="mt-4 w-100" />;
            }

            return (
              <InfoItem
                key={index}
                label={item.label}
                value={item.value}
                col={item.col}
                classname={item.className}
              >
                {item.render}
              </InfoItem>
            );
          })}
        </Row>
      </div>
    </div>
  );
};

export default WashHistoryDetails;
